/*
 * Velaigal shared admin segmented filter — used by the role filter on
 * Users.tsx and both the tab switcher and status filter on Requests.tsx.
 * Extracted because those three were byte-identical inline-styled pills
 * before this fix.
 *
 * Per the design system's radius ceiling (tasks/velaigal-design-system.md:76,
 * "rounded-full — status dots, avatars only"), an interactive filter control
 * doesn't get pill shape — this is a single `border border-hairline
 * rounded-md` strip with a hairline divider between segments. The active
 * segment gets the same `bg-saffron-tint text-ink` treatment Sidebar.tsx
 * uses for its active nav item, so "currently selected" reads the same way
 * across the whole admin surface; inactive segments are plain text with a
 * hairline hover fill.
 */

export interface SegmentedFilterOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedFilterProps<T extends string> {
  options: Array<SegmentedFilterOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}

function SegmentedFilter<T extends string>({ options, value, onChange, ariaLabel }: SegmentedFilterProps<T>) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex border border-hairline rounded-md overflow-hidden">
      {options.map((opt, index) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={[
              'text-sm font-medium px-3.5 py-1.5 transition-colors',
              index > 0 ? 'border-l border-hairline' : '',
              active ? 'bg-saffron-tint text-ink' : 'text-ink-secondary hover:bg-hairline',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedFilter;
